import type { Graph } from './graph';
import { birthKey } from './layout';

/** Oldest first; people with no known birth year come last (by creation order). */
export function byAge(g: Graph, ids: readonly string[] = g.order): string[] {
  const rank = new Map(g.order.map((id, i) => [id, i]));
  return [...ids].sort((a, b) => {
    const ka = birthKey(g.people.get(a));
    const kb = birthKey(g.people.get(b));
    if (ka === null || kb === null) return ka === kb ? rank.get(a)! - rank.get(b)! : ka === null ? 1 : -1;
    return ka - kb || rank.get(a)! - rank.get(b)!;
  });
}

/** Steps from `from` to everyone connected through parent, child or partner links (the person themselves = 0). */
export function distances(g: Graph, from: string): Map<string, number> {
  const out = new Map<string, number>([[from, 0]]);
  let frontier = [from];
  while (frontier.length) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const m of [g.parents, g.children, g.partners]) {
        for (const n of m.get(id) ?? []) {
          if (out.has(n)) continue;
          out.set(n, out.get(id)! + 1);
          next.push(n);
        }
      }
    }
    frontier = next;
  }
  return out;
}

/** Generation number (0 = the eldest line). Partners share a generation; a child is one below its latest parent. */
export function generations(g: Graph): Map<string, number> {
  const gen = new Map<string, number>(g.order.map((id) => [id, 0]));
  // Relax until stable; bounded so a malformed (cyclic) graph can't loop forever.
  for (let pass = 0; pass <= g.order.length; pass++) {
    let changed = false;
    for (const id of g.order) {
      let v = gen.get(id)!;
      for (const p of g.parents.get(id) ?? []) v = Math.max(v, gen.get(p)! + 1);
      for (const p of g.partners.get(id) ?? []) v = Math.max(v, gen.get(p)!);
      if (v !== gen.get(id)) {
        gen.set(id, v);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return gen;
}

export interface OutlineNode {
  /** The person this line is led by. */
  id: string;
  /** Their partners, shown beside them. */
  partners: string[];
  /** Children of this couple, eldest first. */
  children: OutlineNode[];
}

/**
 * The tree as a folder-style outline. Each couple appears once: a person without parents who is partnered
 * with someone who has parents is shown beside that partner rather than as a separate top-level entry.
 * A child whose parents are in different couples appears under whichever is reached first.
 */
export function outline(g: Graph): OutlineNode[] {
  const placed = new Set<string>();
  const hasParents = (id: string) => (g.parents.get(id) ?? []).length > 0;

  const build = (id: string): OutlineNode => {
    placed.add(id);
    // Partners who have no family of their own sit beside this person; others appear under their own parents.
    const partners = (g.partners.get(id) ?? []).filter((p) => !placed.has(p) && !hasParents(p));
    for (const p of partners) placed.add(p);
    const kids = byAge(g, [...new Set([id, ...partners].flatMap((x) => g.children.get(x) ?? []))].filter((k) => !placed.has(k)));
    const children: OutlineNode[] = [];
    for (const k of kids) if (!placed.has(k)) children.push(build(k));
    return { id, partners, children };
  };

  // Top level: people who are born into the tree's eldest couples.
  const tops = g.order.filter((id) => !hasParents(id) && !(g.partners.get(id) ?? []).some((p) => hasParents(p)));
  const roots: OutlineNode[] = [];
  for (const id of byAge(g, tops)) if (!placed.has(id)) roots.push(build(id));
  // Anyone left over (only possible with cyclic data) still gets listed.
  for (const id of g.order) if (!placed.has(id)) roots.push(build(id));
  return roots;
}
